import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ProductDialog from "./AddEditProduct";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import moment from "moment";
import ImagePreviewModal, {
  getProductVideosFromRow,
  getProductImagesFromRow,
} from "./ImagePreviewModal";
import { FaEdit } from "react-icons/fa";
import { FiTrash2 } from "react-icons/fi";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import {
  fetchProductListRequest,
  bookNowStatusChangeRequest,
  deleteProductRequest,
  resetToggleStatusFlags,
  resetDeleteFlags,
  addProductRequest,
  editProductRequest,
} from "../../../redux/Product/ProductListSlice";
import { getBullionRequest } from "../../../redux/Bullion/BullionSlice";
import ConfirmationDialog from "../../Modals/ConfirmationModal";
import TermsConditions from "./TermsConditions";
import { apiRequest } from "../../../redux/Reports/reportsSlice";
import useCsvConverter from "../../../hooks/convertToCSV";
import { canShowPermission } from "../Permissions/permissionObject";

const getProductImages = getProductImagesFromRow;

const CellWrap = ({ children, align = "center" }) => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent:
        align === "left"
          ? "flex-start"
          : align === "right"
            ? "flex-end"
            : "center",
      minWidth: 0,
      py: 0.5,
    }}
  >
    {children}
  </Box>
);

const Products = () => {
  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const stored = localStorage.getItem("userPermissions");
    if (stored) {
      try {
        setPermissions(JSON.parse(stored));
      } catch (e) {
        console.error("Failed to parse userPermissions", e);
      }
    }
  }, []);

  const canCreate = canShowPermission(permissions, "products", "create");
  const canEdit = canShowPermission(permissions, "products", "edit");
  const canDelete = canShowPermission(permissions, "products", "delete");

  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [open, setOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [imageOpen, setImageOpen] = useState(false);
  const [previewData, setPreviewData] = useState({ images: [], videos: [], productName: "" });
  const [bookNowLoadingId, setBookNowLoadingId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [productIdToDelete, setProductIdToDelete] = useState(null);

  const bookNowInFlight = useRef(new Set());
  const dispatch = useDispatch();

  const productlistState = useSelector(
    (state) => state.productlist,
    shallowEqual,
  );
  const productlist = productlistState?.data ?? [];
  const toggleStatusSuccess = productlistState?.toggleStatusSuccess;
  const deleteSuccess = productlistState?.deleteSuccess;
  const addSuccess = productlistState?.addSuccess;
  const editSuccess = productlistState?.editSuccess;

  const metalsenabled = useSelector(
    (state) => state?.bullions?.successData?.data?.bulionRates,
  );

  useEffect(() => {
    dispatch(getBullionRequest());
    dispatch(fetchProductListRequest());
  }, [dispatch]);

  useEffect(() => {
    if (toggleStatusSuccess || deleteSuccess || addSuccess || editSuccess) {
      dispatch(fetchProductListRequest());
      dispatch(resetToggleStatusFlags());
      dispatch(resetDeleteFlags());

      if (bookNowLoadingId) {
        bookNowInFlight.current.delete(bookNowLoadingId);
        setBookNowLoadingId(null);
      }
    }
  }, [
    toggleStatusSuccess,
    deleteSuccess,
    addSuccess,
    editSuccess,
    dispatch,
    bookNowLoadingId,
  ]);

  const handleViewClick = useCallback((row) => {
    setPreviewData({
      images: getProductImagesFromRow(row),
      videos: getProductVideosFromRow(row),
      productName: row?.name || "",
    });
    setImageOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setImageOpen(false);
    setPreviewData({ images: [], videos: [], productName: "" });
  }, []);

  const updateBookNowStatus = useCallback(
    ({ productId, enabled }) => {
      if (bookNowInFlight.current.has(productId)) {
        return;
      }

      bookNowInFlight.current.add(productId);
      setBookNowLoadingId(productId);

      dispatch(
        bookNowStatusChangeRequest({
          productId: { productId },
          enabled: !enabled,
        }),
      );
    },
    [dispatch],
  );

  const deleteProduct = useCallback(
    (id) => {
      dispatch(deleteProductRequest({ id }));
    },
    [dispatch],
  );

  const columns = useMemo(
    () => [
      {
        flex: 0.12,
        minWidth: 110,
        headerName: "Created",
        field: "createdAt",
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <CellWrap align="center">
            <Typography variant="body2" sx={{ fontWeight: 500, color: "#475569" }}>
              {moment(row.createdAt).format("DD MMM YYYY")}
            </Typography>
          </CellWrap>
        ),
      },
      {
        flex: 0.1,
        minWidth: 90,
        headerName: "Media",
        field: "url",
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: ({ row }) => {
          const images = getProductImagesFromRow(row);
          const videos = getProductVideosFromRow(row);
          const thumb = images[0];
          const hasVideos = videos.length > 0;
          const mediaCount = images.length + videos.length;

          if (!thumb && !hasVideos) {
            return (
              <CellWrap align="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "#f1f5f9",
                    border: "1px dashed #cbd5e1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#94a3b8",
                    flexShrink: 0,
                  }}
                >
                  <ImageOutlinedIcon fontSize="small" />
                </Box>
              </CellWrap>
            );
          }

          return (
            <CellWrap align="center">
              <Tooltip title="Click to preview images & videos">
                <Box
                  onClick={() => handleViewClick(row)}
                  sx={{
                    position: "relative",
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    overflow: "hidden",
                    cursor: "pointer",
                    border: "2px solid #e2e8f0",
                    flexShrink: 0,
                    transition: "all 0.2s ease",
                    bgcolor: thumb ? "transparent" : "#0f172a",
                    "&:hover": {
                      borderColor: "#752ADA",
                      boxShadow: "0 4px 12px rgba(117,42,218,0.2)",
                    },
                  }}
                >
                  {thumb ? (
                    <Box
                      component="img"
                      src={thumb}
                      alt={row.name}
                      sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                      }}
                    >
                      <PlayCircleOutlineIcon />
                    </Box>
                  )}
                  {mediaCount > 1 && (
                    <Chip
                      label={`+${mediaCount - 1}`}
                      size="small"
                      sx={{
                        position: "absolute",
                        bottom: 2,
                        right: 2,
                        height: 18,
                        fontSize: 10,
                        fontWeight: 700,
                        bgcolor: "rgba(117,42,218,0.9)",
                        color: "#fff",
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  )}
                  {hasVideos && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 2,
                        left: 2,
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        bgcolor: "rgba(15,23,42,0.75)",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <VideocamOutlinedIcon sx={{ fontSize: 12 }} />
                    </Box>
                  )}
                </Box>
              </Tooltip>
            </CellWrap>
          );
        },
      },
      {
        flex: 0.18,
        minWidth: 140,
        field: "name",
        headerName: "Product Name",
        align: "left",
        headerAlign: "left",
        renderCell: ({ row }) => (
          <CellWrap align="left">
            <Tooltip title={row.name}>
              <Typography variant="body2" fontWeight={600} noWrap sx={{ color: "#1e293b", width: "100%" }}>
                {row.name}
              </Typography>
            </Tooltip>
          </CellWrap>
        ),
      },
      {
        flex: 0.1,
        field: "weight",
        minWidth: 90,
        headerName: "Weight",
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <CellWrap align="center">
            <Chip
              label={`${row.metal_weight} g`}
              size="small"
              sx={{ bgcolor: "#f8fafc", fontWeight: 500 }}
            />
          </CellWrap>
        ),
      },
      {
        flex: 0.12,
        minWidth: 110,
        headerName: "Rate/g",
        field: "ratePerGrm",
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <CellWrap align="center">
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#752ADA" }}>
              ₹{row?.bullion?.bullionRate?.ratePerGram ?? "-"}
            </Typography>
          </CellWrap>
        ),
      },
      {
        flex: 0.18,
        minWidth: 140,
        headerName: "Description",
        field: "desc",
        align: "left",
        headerAlign: "left",
        renderCell: ({ row }) => (
          <CellWrap align="left">
            <Tooltip title={row?.desc || "-"}>
              <Typography variant="body2" noWrap sx={{ color: "#64748b", width: "100%" }}>
                {row?.desc ?? "-"}
              </Typography>
            </Tooltip>
          </CellWrap>
        ),
      },
      {
        flex: 0.15,
        minWidth: 120,
        field: "tags",
        headerName: "Tags",
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <CellWrap align="center">
            <Tooltip title={row.tags?.join(", ") || "No tags"}>
              <Typography variant="caption" noWrap sx={{ color: "#64748b", width: "100%", textAlign: "center" }}>
                {row.tags?.length ? row.tags.join(", ") : "—"}
              </Typography>
            </Tooltip>
          </CellWrap>
        ),
      },
      {
        flex: 0.12,
        minWidth: 110,
        headerName: "Book Now",
        field: "booknow",
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          const isLoading =
            bookNowLoadingId === row._id ||
            bookNowInFlight.current.has(row._id);
          if (!canEdit) {
            return (
              <CellWrap align="center">
                <Chip
                  label={row.booknow ? "Enabled" : "Disabled"}
                  size="small"
                  color={row.booknow ? "success" : "default"}
                  variant="outlined"
                />
              </CellWrap>
            );
          }
          return (
            <CellWrap align="center">
              <Button
                size="small"
                variant={row.booknow ? "contained" : "outlined"}
                disabled={isLoading}
                onClick={() =>
                  updateBookNowStatus({
                    productId: row._id,
                    enabled: row.booknow,
                  })
                }
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  minWidth: 88,
                  ...(row.booknow
                    ? { bgcolor: "#059669", "&:hover": { bgcolor: "#047857" } }
                    : { color: "#dc2626", borderColor: "#fca5a5" }),
                }}
              >
                {isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : row.booknow ? (
                  "Enabled"
                ) : (
                  "Disabled"
                )}
              </Button>
            </CellWrap>
          );
        },
      },
      ...(canEdit || canDelete
        ? [
            {
              flex: 0.1,
              minWidth: 100,
              headerName: "Actions",
              field: "actions",
              align: "center",
              headerAlign: "center",
              sortable: false,
              renderCell: ({ row }) => (
                <CellWrap align="center">
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    {canEdit && (
                      <Tooltip title="Edit product">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditProduct(row);
                            setOpen(true);
                          }}
                          sx={{
                            color: "#752ADA",
                            bgcolor: "rgba(117,42,218,0.08)",
                            "&:hover": { bgcolor: "rgba(117,42,218,0.15)" },
                          }}
                        >
                          <FaEdit size={14} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip title="Delete product">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setProductIdToDelete(row._id);
                            setConfirmOpen(true);
                          }}
                          sx={{
                            color: "#dc2626",
                            bgcolor: "rgba(220,38,38,0.08)",
                            "&:hover": { bgcolor: "rgba(220,38,38,0.15)" },
                          }}
                        >
                          <FiTrash2 size={14} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </CellWrap>
              ),
            },
          ]
        : []),
    ],
    [
      bookNowLoadingId,
      updateBookNowStatus,
      handleViewClick,
      canEdit,
      canDelete,
    ],
  );

  const { convertToCsv } = useCsvConverter();

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadProductCsv = () => {
    dispatch(
      apiRequest({
        method: "GET",
        endpoint: "/api/v1/product/productsCsv",
        query: {},
        callback: (response) => {
          const csv = convertToCsv(response?.data || []);
          triggerCsvDownload(csv, "products.csv");
        },
      }),
    );
  };

  const filteredProducts = useMemo(() => {
    if (!search) return productlist;

    const lowerSearch = search.toLowerCase();

    return productlist.filter((row) => {
      return (
        row.name?.toLowerCase().includes(lowerSearch) ||
        row.desc?.toLowerCase().includes(lowerSearch) ||
        row.metal_weight?.toString().includes(lowerSearch) ||
        row?.bullion?.bullionRate?.ratePerGram
          ?.toString()
          .includes(lowerSearch) ||
        row.tags?.join(" ").toLowerCase().includes(lowerSearch) ||
        moment(row.createdAt)
          .format("DD-MM-YYYY")
          .toLowerCase()
          .includes(lowerSearch)
      );
    });
  }, [search, productlist]);

  return (
    <Box sx={{ width: "100%", p: { xs: 1, md: 2 } }}>
      <Card
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 3,
          border: "1px solid #e8eaf0",
          boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
        }}
      >
        <TermsConditions />
      </Card>

      <Card
        sx={{
          borderRadius: 3,
          border: "1px solid #e8eaf0",
          boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            background: "linear-gradient(135deg, #752ADA 0%, #9b5de5 100%)",
            color: "#fff",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Products
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {filteredProducts.length} product{filteredProducts.length !== 1 ? "s" : ""} · Click media to preview images & videos
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadProductCsv}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.5)",
                textTransform: "none",
                "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              Download CSV
            </Button>
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpen(true)}
                sx={{
                  bgcolor: "#fff",
                  color: "#752ADA",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#f8fafc" },
                }}
              >
                Add Product
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ p: 2.5 }}>
          <TextField
            placeholder="Search by name, description, weight, tags..."
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2.5, maxWidth: 420 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />

          <DataGrid
            autoHeight
            rows={filteredProducts}
            getRowId={(row) => row._id}
            columns={columns}
            pageSize={pageSize}
            rowHeight={64}
            disableSelectionOnClick
            rowsPerPageOptions={[10, 25, 50]}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
                minHeight: "48px !important",
                maxHeight: "48px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                color: "#475569",
              },
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
                borderColor: "#f1f5f9",
                py: 0,
              },
              "& .MuiDataGrid-row": {
                maxHeight: "none !important",
              },
              "& .MuiDataGrid-row:hover": {
                bgcolor: "rgba(117,42,218,0.04)",
              },
            }}
          />
        </Box>
      </Card>

      <ProductDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setEditProduct(null);
        }}
        onSubmit={(data) => {
          if (editProduct?._id) {
            dispatch(editProductRequest({ id: editProduct._id, data }));
          } else {
            dispatch(addProductRequest(data));
          }
        }}
        metalsenabled={metalsenabled}
        editData={editProduct}
      />
      <ImagePreviewModal
        open={imageOpen}
        handleClose={handleClose}
        images={previewData.images}
        videos={previewData.videos}
        imageUrl={previewData.images[0]}
        productName={previewData.productName}
        title="Product Media Preview"
      />
      <ConfirmationDialog
        title="Delete Product?"
        content="Are you sure you want to delete the product"
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          if (productIdToDelete) {
            deleteProduct(productIdToDelete);
            setProductIdToDelete(null);
          }
        }}
      />
    </Box>
  );
};

export default Products;
